// https://github.com/synapticon/somanet_software/blob/32edf5ef572ea7d840aabe4a26e0f3d31f05caab/sw_applications/app_motion_drive/test/pytest/somanet_icmu_helpers.py

import { HardwareDescription } from './hardware-description';
import { findSomanetProductById, resolveEncoderProductId } from './product';
import { MotionMasterMessage } from './types';

/**
 * One field of an iC-MU register: a named group of bits, as the datasheet names and describes it.
 */
export interface IcMuRegisterField {
  /** The datasheet's name of the field, e.g. `GC_M`. */
  name: string;
  /**
   * The slice of the field this register carries, as the datasheet's register map prints it, e.g. `1:0`.
   * Empty for a one-bit field. It is the field's own bits, not their position in the byte: a value wider
   * than a register is spread across several, which is why `RESABZ` is `7:0` at 0x13 and `15:8` at 0x14.
   */
  bits: string;
  /** The most significant bit the field occupies in the register byte. */
  msb: number;
  /** The least significant bit the field occupies in the register byte. */
  lsb: number;
  /** The datasheet's one-line description of the field. */
  description: string;
}

/**
 * A key configuration register of the iC-MU in a Circulo integrated encoder: the address the drive
 * reads it at in RAM, the address it is stored at in EEPROM, and the fields it holds.
 */
export interface CirculoIntegratedEncoderKeyConfigurationRegister {
  /** The register address in RAM, which the encoder register communication OS command reads. */
  ram: number;
  /** The register address in EEPROM. */
  eeprom: number;
  /** The fields of the register, most significant bit first. Bits no field names are unused. */
  fields: IcMuRegisterField[];
  /**
   * Whether the encoder calibration writes this register: the analog track adjustments of the master and the
   * Nonius track (`GX`, `VOSS`, `VOSC`, `PH`, with `PHR` in the same register as `PH`) and the Nonius track
   * offset table (`SPO_BASE`, `SPO_0` to `SPO_14`).
   * Their values differ from encoder to encoder, because each ring is mounted slightly differently.
   * The values of the other registers are the same on every encoder of a kind.
   */
  writtenByCalibration: boolean;
}

/** A field of a register, with the datasheet's own slice of the field computed from its width. */
function icMuField(name: string, msb: number, lsb: number, description: string, bits?: string): IcMuRegisterField {
  return { name, bits: bits ?? (msb === lsb ? '' : `${msb - lsb}:0`), msb, lsb, description };
}

function keyRegister(
  ram: number,
  eeprom: number,
  fields: IcMuRegisterField[],
  writtenByCalibration = false
): CirculoIntegratedEncoderKeyConfigurationRegister {
  return { ram, eeprom, fields, writtenByCalibration };
}

const resAbzDescription = 'Incremental interface resolution for ABZ, STEP/DIR and CW/CCW';
const spoDescription = 'Offset of Nonius to master';

/**
 * The 33 key configuration registers of the iC-MU in a Circulo integrated encoder, with their fields.
 *
 * Registers 0x00 to 0x18 have the same address in RAM and EEPROM. RAM registers 0x52 to 0x59, the Nonius
 * offset table, are stored at EEPROM addresses 0x19 to 0x20.
 *
 * Names and descriptions are transcribed from the iC-MU Series datasheet Rev B1, Tables 90 and 91, as
 * Motion Master v6 carries them (`libs/node/ic_haus_registers.cc`). The bit positions follow
 * {@link CirculoIntegratedEncoderIcmuParameters}.
 */
export const circuloIntegratedEncoderKeyConfigurationRegisters: CirculoIntegratedEncoderKeyConfigurationRegister[] = [
  keyRegister(0x00, 0x00, [
    icMuField('GC_M', 7, 6, 'Master gain range selection'),
    icMuField('GF_M', 5, 0, 'Master gain'),
  ]),
  keyRegister(0x01, 0x01, [icMuField('GX_M', 6, 0, 'Master cosine signal gain adjustment')], true),
  keyRegister(0x02, 0x02, [icMuField('VOSS_M', 6, 0, 'Master sine offset adjustment')], true),
  keyRegister(0x03, 0x03, [icMuField('VOSC_M', 6, 0, 'Master cosine offset adjustment')], true),
  keyRegister(
    0x04,
    0x04,
    [icMuField('PHR_M', 7, 7, 'Master phase adjustment range'), icMuField('PH_M', 6, 0, 'Master phase adjustment')],
    true
  ),
  keyRegister(0x05, 0x05, [
    icMuField('ENAC', 7, 7, 'Amplitude control unit activation'),
    icMuField('CIBM', 3, 0, 'Bias current settings'),
  ]),
  keyRegister(0x06, 0x06, [
    icMuField('GC_N', 7, 6, 'Nonius gain range selection'),
    icMuField('GF_N', 5, 0, 'Nonius gain'),
  ]),
  keyRegister(0x07, 0x07, [icMuField('GX_N', 6, 0, 'Nonius cosine signal gain adjustment')], true),
  keyRegister(0x08, 0x08, [icMuField('VOSS_N', 6, 0, 'Nonius sine offset adjustment')], true),
  keyRegister(0x09, 0x09, [icMuField('VOSC_N', 6, 0, 'Nonius cosine offset adjustment')], true),
  keyRegister(
    0x0a,
    0x0a,
    [icMuField('PHR_N', 7, 7, 'Nonius phase adjustment range'), icMuField('PH_N', 6, 0, 'Nonius phase adjustment')],
    true
  ),
  keyRegister(0x0b, 0x0b, [
    icMuField('MODEB', 6, 4, 'I/O port B configuration'),
    icMuField('NTOA', 3, 3, 'Adaptive timeout'),
    icMuField('MODEA', 2, 0, 'I/O port A configuration'),
  ]),
  keyRegister(0x0c, 0x0c, [icMuField('CFGEW', 7, 0, 'Error and warning bit configuration')]),
  keyRegister(0x0d, 0x0d, [
    icMuField('ACC_STAT', 7, 7, 'Output configuration of the status register'),
    icMuField('NCHK_CRC', 6, 6, 'Cyclic check of CRC16 and CRC8'),
    icMuField('NCHK_NON', 5, 5, 'Cyclic check of the Nonius value (low active)'),
    icMuField('ACRM_RES', 4, 4, 'Automatic reset with master track amplitude errors'),
    icMuField('EMTD', 2, 0, 'Minimum error message duration'),
  ]),
  keyRegister(0x0e, 0x0e, [
    icMuField('ESSI_MT', 7, 6, 'Error bit external multiturn'),
    icMuField('ROT_MT', 5, 5, 'Code direction external multiturn'),
    icMuField('LIN', 4, 4, 'Linear scanning'),
    icMuField('FILT', 2, 0, 'Digital filter settings'),
  ]),
  keyRegister(0x0f, 0x0f, [
    icMuField('SPO_MT', 7, 4, 'Offset external multiturn'),
    icMuField('MPC', 3, 0, 'Master period count'),
  ]),
  keyRegister(0x10, 0x10, [
    icMuField('GET_MT', 7, 7, 'Multiturn interface daisy chain'),
    icMuField('CHK_MT', 6, 6, 'Cyclic check of the multiturn value'),
    icMuField('SBL_MT', 5, 4, 'Multiturn synchronization bit length'),
    icMuField('MODE_MT', 3, 0, 'Multiturn mode'),
  ]),
  keyRegister(0x11, 0x11, [
    icMuField(
      'OUT_ZERO',
      7,
      5,
      'Output shift register configuration: zeros inserted after the used bits and before an error or warning'
    ),
    icMuField('OUT_MSB', 4, 0, 'Output shift register configuration: MSB used bits'),
  ]),
  keyRegister(0x12, 0x12, [
    icMuField('GSSI', 7, 7, 'Gray or binary data format'),
    icMuField('RSSI', 6, 6, 'Ring operation'),
    icMuField('MODE_ST', 5, 4, 'Data output'),
    icMuField('OUT_LSB', 3, 0, 'Output shift register configuration: LSB used bits'),
  ]),
  keyRegister(0x13, 0x13, [icMuField('RESABZ', 7, 0, resAbzDescription, '7:0')]),
  keyRegister(0x14, 0x14, [icMuField('RESABZ', 7, 0, resAbzDescription, '15:8')]),
  keyRegister(0x15, 0x15, [
    icMuField('ROT_ALL', 7, 7, 'Code direction'),
    icMuField('SS_AB', 5, 4, 'System AB step size'),
    icMuField('ENIF_AUTO', 3, 3, 'Incremental interface enable'),
    icMuField('FRQAB', 2, 0, 'AB output frequency'),
  ]),
  keyRegister(0x16, 0x16, [
    icMuField('LENZ', 7, 6, 'Index pulse length'),
    icMuField('CHYS_AB', 5, 4, 'Converter hysteresis'),
    icMuField('PP60UVW', 3, 3, 'Commutation signal phase position'),
    icMuField('INV_A', 2, 2, 'A, STEP or CW signal inversion'),
    icMuField('INV_B', 1, 1, 'B, DIR or CCW signal inversion'),
    icMuField('INV_Z', 0, 0, 'Z or NCLR signal inversion'),
  ]),
  keyRegister(0x17, 0x17, [
    icMuField('RPL', 7, 6, 'Register access control'),
    icMuField('PPUVW', 5, 0, 'Number of commutation signal pole pairs'),
  ]),
  keyRegister(0x18, 0x18, [icMuField('TEST', 7, 0, 'Adjustment modes and iC-Haus test modes')]),
  keyRegister(
    0x52,
    0x19,
    [icMuField('SPO_0', 7, 4, spoDescription), icMuField('SPO_BASE', 3, 0, spoDescription)],
    true
  ),
  keyRegister(0x53, 0x1a, [icMuField('SPO_2', 7, 4, spoDescription), icMuField('SPO_1', 3, 0, spoDescription)], true),
  keyRegister(0x54, 0x1b, [icMuField('SPO_4', 7, 4, spoDescription), icMuField('SPO_3', 3, 0, spoDescription)], true),
  keyRegister(0x55, 0x1c, [icMuField('SPO_6', 7, 4, spoDescription), icMuField('SPO_5', 3, 0, spoDescription)], true),
  keyRegister(0x56, 0x1d, [icMuField('SPO_8', 7, 4, spoDescription), icMuField('SPO_7', 3, 0, spoDescription)], true),
  keyRegister(0x57, 0x1e, [icMuField('SPO_10', 7, 4, spoDescription), icMuField('SPO_9', 3, 0, spoDescription)], true),
  keyRegister(0x58, 0x1f, [icMuField('SPO_12', 7, 4, spoDescription), icMuField('SPO_11', 3, 0, spoDescription)], true),
  keyRegister(0x59, 0x20, [icMuField('SPO_14', 7, 4, spoDescription), icMuField('SPO_13', 3, 0, spoDescription)], true),
];

/**
 * Mapping of key configuration register addresses for Circulo Integrated Encoders (e.g., iC-MU, iC-PVL).
 *
 * Each entry maps a configuration register's address in EEPROM to its corresponding
 * address in RAM. This mapping allows read/write access to configuration data via
 * either persistent storage (EEPROM) or volatile memory (RAM).
 *
 * Derived from {@link circuloIntegratedEncoderKeyConfigurationRegisters}, in the same order.
 */
export const circuloIntegratedEncoderKeyConfigurationRegisterAddresses: { eeprom: number; ram: number }[] =
  circuloIntegratedEncoderKeyConfigurationRegisters.map(({ eeprom, ram }) => ({ eeprom, ram }));

/** A register field together with the raw value its bits hold. */
export interface IcMuRegisterFieldValue extends IcMuRegisterField {
  value: number;
}

/** The iC-MU parts `HARD_REV` (register 0x74) names. */
export type IcMuPartName = 'iC-MU' | 'iC-MU128' | 'iC-MU150' | 'iC-MU200';

/** The iC-MU chip and its hardware revision, from `HARD_REV` (register 0x74). */
export interface IcMuPart {
  /** The raw value of `HARD_REV`. */
  hardRev: number;
  /** The chip, or null for a code that names no iC-MU. */
  name: IcMuPartName | null;
  /** The hardware revision: the lower nibble of `HARD_REV`. */
  revision: number;
}

/** The register that names the iC-MU chip and its hardware revision. */
export const icMuHardRevRegister = 0x74;

/**
 * Names the iC-MU chip from `HARD_REV` (register 0x74). From the iC-MU150 on, the upper nibble names the chip;
 * the upper nibble 0 covers the plain iC-MU, 0x05 to 0x07, and the iC-MU128, 0x09 and 0x0A, as the iC-Haus
 * library (`MU_3SL_defs.h`) and Motion Master name them.
 */
export function decodeIcMuHardRev(hardRev: number): IcMuPart {
  const code = hardRev & 0xff;
  let name: IcMuPartName | null = null;
  if (code >= 0x05 && code <= 0x07) {
    name = 'iC-MU';
  } else if (code === 0x09 || code === 0x0a) {
    name = 'iC-MU128';
  } else if (code >> 4 === 0x1) {
    name = 'iC-MU150';
  } else if (code >> 4 === 0x2) {
    name = 'iC-MU200';
  }
  return { hardRev: code, name, revision: code & 0x0f };
}

/**
 * A register the report reads besides the key configuration registers. It lives in RAM only: the encoder
 * does not store it in EEPROM, and it is not part of the recovery file.
 */
export interface CirculoIntegratedEncoderStatusRegister {
  /** The register address in RAM (bank 0, as the key configuration registers 0x00 to 0x18). */
  ram: number;
  /** The fields of the register, most significant bit first. */
  fields: IcMuRegisterField[];
}

/**
 * The gain the amplitude control unit currently sets, for the master and the Nonius track. It is set only
 * while `ENAC` (register 0x05, bit 7) is 1; the report gives the raw value either way.
 */
export const circuloIntegratedEncoderAmplitudeControlRegisters: CirculoIntegratedEncoderStatusRegister[] = [
  {
    ram: 0x2b,
    fields: [
      icMuField('ACGAIN_M', 4, 3, 'Master gain range currently set by the amplitude control unit'),
      icMuField('AFGAIN_M', 2, 0, 'Master gain factor currently set by the amplitude control unit'),
    ],
  },
  {
    ram: 0x2f,
    fields: [
      icMuField('ACGAIN_N', 4, 3, 'Nonius gain range currently set by the amplitude control unit'),
      icMuField('AFGAIN_N', 2, 0, 'Nonius gain factor currently set by the amplitude control unit'),
    ],
  },
];

/** A key configuration register with the raw value read from it and the raw values of its fields. */
export interface CirculoIntegratedEncoderReportRegister {
  ram: number;
  eeprom: number;
  writtenByCalibration: boolean;
  value: number;
  fields: IcMuRegisterFieldValue[];
}

/** A RAM-only register with the raw value read from it and the raw values of its fields. */
export interface CirculoIntegratedEncoderReportStatusRegister {
  ram: number;
  value: number;
  fields: IcMuRegisterFieldValue[];
}

/** The version of {@link CirculoIntegratedEncoderReport}. It changes when the shape changes. */
export const circuloIntegratedEncoderReportSchemaVersion = 2;

/**
 * The registers of a Circulo integrated encoder as read: the data behind both the JSON and the text report.
 *
 * Every value is raw. The report does not decode values with a datasheet, since datasheets and chip
 * revisions change; decoding happens offline, with the chip revision the report gives.
 */
export interface CirculoIntegratedEncoderReport {
  schemaVersion: typeof circuloIntegratedEncoderReportSchemaVersion;
  encoderOrdinal: number;
  /** The serial number of the device, or null when it isn't known. */
  deviceSerialNumber: string | null;
  /** When the registers were read, as an ISO 8601 UTC time. */
  readAt: string;
  /** The chip and its revision from `HARD_REV` (register 0x74), or null when it was not read. */
  chip: IcMuPart | null;
  /**
   * Whether health monitoring is on: whether `NON_CTR` is reported in the error bit. See
   * {@link isCirculoIntegratedEncoderHealthMonitoringActive}.
   */
  healthMonitoring: { active: boolean; cfgew: number };
  /** The Nonius track offset table, `SPO_BASE` and `SPO_0` to `SPO_14`, as the raw 4-bit values. */
  noniusTrackOffsetTable: { name: string; value: number }[];
  /**
   * `ACGAIN` and `AFGAIN` of both tracks (registers 0x2B and 0x2F), or an empty list when they were not read.
   */
  amplitudeControlRegisters: CirculoIntegratedEncoderReportStatusRegister[];
  /** Every key configuration register in the order of {@link circuloIntegratedEncoderKeyConfigurationRegisters}. */
  registers: CirculoIntegratedEncoderReportRegister[];
}

function hex(value: number, digits = 2): string {
  return `0x${value.toString(16).toUpperCase().padStart(digits, '0')}`;
}

/** The raw value of each field of a register byte. */
function fieldValues(fields: IcMuRegisterField[], value: number): IcMuRegisterFieldValue[] {
  return fields.map((field) => ({
    ...field,
    value: ((value & 0xff) >> field.lsb) & ((1 << (field.msb - field.lsb + 1)) - 1),
  }));
}

/** The bit of `CFGEW` (register 0x0C) that keeps the `NON_CTR` error out of the error bit when set. */
const cfgewNonCtrMask = 0x40;

/**
 * Whether health monitoring is on. Health monitoring is Synapticon's name for reporting the iC-MU's
 * `NON_CTR` error, the period counter consistency error, in the error bit.
 *
 * `CFGEW` (register 0x0C) bit 6 enables `NON_CTR` in the error bit when 0 and disables it when 1. Motion Master
 * writes 0x6C by default, which disables it, and 0x2C when a full calibration is asked to activate health
 * monitoring. The two differ in bit 6 only.
 *
 * @param cfgew - The value of register 0x0C.
 */
export function isCirculoIntegratedEncoderHealthMonitoringActive(cfgew: number): boolean {
  return (cfgew & cfgewNonCtrMask) === 0;
}

/**
 * Splits the key configuration register values into the raw values of their fields.
 *
 * @param values - The register values, in the order of {@link circuloIntegratedEncoderKeyConfigurationRegisters}.
 * @returns For each register, its fields, most significant bit first, each with its raw value.
 * @throws Error when the number of values does not match the number of key configuration registers.
 */
export function decodeCirculoIntegratedEncoderKeyConfigurationRegisters(values: number[]): IcMuRegisterFieldValue[][] {
  const registers = circuloIntegratedEncoderKeyConfigurationRegisters;
  if (values.length !== registers.length) {
    throw new Error(`Expected ${registers.length} register values, got ${values.length}.`);
  }
  return registers.map((register, i) => fieldValues(register.fields, values[i]));
}

/** The encoder and device a report is made for, when its registers were read, and the registers read besides. */
export interface CirculoIntegratedEncoderReportInfo {
  encoderOrdinal: number;
  /** The serial number of the device, when it is known. */
  deviceSerialNumber?: string;
  /** When the registers were read. */
  readAt: Date;
  /** The value of `HARD_REV` (register 0x74), when it was read. */
  hardRev?: number;
  /**
   * The values of {@link circuloIntegratedEncoderAmplitudeControlRegisters}, in their order, when they were read.
   */
  amplitudeControlValues?: number[];
}

/**
 * Builds the report of a Circulo integrated encoder from its registers: the chip revision, health monitoring,
 * the Nonius track offset table, the amplitude control gain and every key configuration register with the raw
 * values of its fields. Nothing is decoded with a datasheet.
 *
 * Serialize it with `JSON.stringify` for programs, or render it with
 * {@link formatCirculoIntegratedEncoderReport} for people.
 *
 * @param values - The register values, in the order of {@link circuloIntegratedEncoderKeyConfigurationRegisters}.
 * @param info - The encoder and device the values were read from, when, and the registers read besides.
 * @throws Error when the number of values does not match the number of registers.
 */
export function buildCirculoIntegratedEncoderReport(
  values: number[],
  info: CirculoIntegratedEncoderReportInfo
): CirculoIntegratedEncoderReport {
  const decoded = decodeCirculoIntegratedEncoderKeyConfigurationRegisters(values);
  const fields = decoded.flat();
  const code = (name: string): number => {
    const field = fields.find((f) => f.name === name);
    if (!field) {
      throw new Error(`No key configuration register field is named ${name}.`);
    }
    return field.value;
  };

  const amplitudeControlValues = info.amplitudeControlValues ?? [];
  const amplitudeControl = circuloIntegratedEncoderAmplitudeControlRegisters;
  if (amplitudeControlValues.length !== 0 && amplitudeControlValues.length !== amplitudeControl.length) {
    throw new Error(
      `Expected ${amplitudeControl.length} amplitude control register values, got ${amplitudeControlValues.length}.`
    );
  }

  const cfgew = code('CFGEW');
  const spoNames = ['SPO_BASE', ...Array.from({ length: 15 }, (_, i) => `SPO_${i}`)];

  return {
    schemaVersion: circuloIntegratedEncoderReportSchemaVersion,
    encoderOrdinal: info.encoderOrdinal,
    deviceSerialNumber: info.deviceSerialNumber ?? null,
    readAt: info.readAt.toISOString(),
    chip: info.hardRev === undefined ? null : decodeIcMuHardRev(info.hardRev),
    healthMonitoring: { active: isCirculoIntegratedEncoderHealthMonitoringActive(cfgew), cfgew },
    noniusTrackOffsetTable: spoNames.map((name) => ({ name, value: code(name) })),
    amplitudeControlRegisters: amplitudeControlValues.map((value, i) => ({
      ram: amplitudeControl[i].ram,
      value: value & 0xff,
      fields: fieldValues(amplitudeControl[i].fields, value),
    })),
    registers: circuloIntegratedEncoderKeyConfigurationRegisters.map((register, i) => ({
      ram: register.ram,
      eeprom: register.eeprom,
      writtenByCalibration: register.writtenByCalibration,
      value: values[i] & 0xff,
      fields: decoded[i],
    })),
  };
}

/**
 * The file name a report is saved under, e.g.
 * `circulo-integrated-encoder-1-report-8502-03-0000001-2540-2026-11-04T10-00-00-000Z.json`.
 *
 * @param format - `json` or `txt`, which is also the extension.
 */
export function circuloIntegratedEncoderReportFileName(
  report: Pick<CirculoIntegratedEncoderReport, 'encoderOrdinal' | 'deviceSerialNumber' | 'readAt'>,
  format: 'json' | 'txt'
): string {
  const name = [
    'circulo-integrated-encoder',
    report.encoderOrdinal,
    'report',
    ...(report.deviceSerialNumber ? [report.deviceSerialNumber] : []),
    report.readAt.replace(/[:.]/g, '-'),
  ].join('-');
  return `${name}.${format}`;
}

/** The chip line of the text report, e.g. `HARD_REV: 0x11 = iC-MU150 rev 1`. */
export function formatIcMuChip(chip: IcMuPart | null): string {
  if (!chip) {
    return 'not read';
  }
  return `${hex(chip.hardRev)} = ${chip.name ?? 'unknown iC-MU chip'} rev ${chip.revision}`;
}

/** The table header and lines of registers with their fields, the first columns given per register. */
function registerTableLines(
  heading: string,
  registers: { head: string; value: number; fields: IcMuRegisterFieldValue[] }[]
): string[] {
  const lines = [
    heading +
      'VALUE'.padEnd(6) +
      'BINARY'.padEnd(12) +
      'FIELD'.padEnd(11) +
      'BITS'.padEnd(6) +
      'FIELD VALUE'.padEnd(13) +
      'DESCRIPTION',
  ];
  for (const register of registers) {
    const head = `${register.head}${hex(register.value)}  ${register.value.toString(2).padStart(8, '0')}    `;
    register.fields.forEach((field, j) => {
      const fieldValue = `${field.value} (${hex(field.value, 1)})`;
      lines.push(
        `${j === 0 ? head : ' '.repeat(head.length)}${field.name.padEnd(11)}${field.bits.padEnd(6)}${fieldValue.padEnd(
          13
        )}${field.description}`.trimEnd()
      );
    });
  }
  return lines;
}

/**
 * Renders a report as plain text for people. It computes nothing: every value comes from the report, raw.
 *
 * It opens with the device and the chip revision, then whether health monitoring is on, the Nonius track
 * offset table, the amplitude control gain, and every key configuration register with its RAM and EEPROM
 * address and its value, and below it every field with its name, its slice, its value and its description.
 * Registers the encoder calibration writes are marked with `*`.
 *
 * @returns The report, lines separated by `\n` and ending with a newline.
 */
export function formatCirculoIntegratedEncoderReport(report: CirculoIntegratedEncoderReport): string {
  const { healthMonitoring } = report;
  const lines = [
    `Circulo Integrated Encoder ${report.encoderOrdinal} - Key Configuration Registers`,
    `Device serial number: ${report.deviceSerialNumber ?? 'unknown'}`,
    `Read at: ${report.readAt}`,
    `HARD_REV: ${formatIcMuChip(report.chip)}`,
    'All values are raw, as read from the encoder.',
    '',
    `HEALTH MONITORING: ${healthMonitoring.active ? 'ON' : 'OFF'} (CFGEW = ${hex(healthMonitoring.cfgew)}, bit 6 = ${
      healthMonitoring.active ? 0 : 1
    })`,
    '',
    'NONIUS TRACK OFFSET TABLE',
    'FIELD'.padEnd(11) + 'VALUE',
    ...report.noniusTrackOffsetTable.map(({ name, value }) => `${name.padEnd(11)}${value} (${hex(value, 1)})`),
    '',
    'AMPLITUDE CONTROL GAIN',
  ];

  if (report.amplitudeControlRegisters.length === 0) {
    lines.push('Not read.');
  } else {
    lines.push(
      ...registerTableLines(
        'RAM'.padEnd(6),
        report.amplitudeControlRegisters.map((register) => ({ ...register, head: `${hex(register.ram)}  ` }))
      )
    );
  }

  lines.push(
    '',
    'REGISTERS',
    'Registers marked * are written by the encoder calibration.',
    '',
    ...registerTableLines(
      'RAM'.padEnd(6) + 'EEPROM'.padEnd(8),
      report.registers.map((register) => ({
        ...register,
        head: `${hex(register.ram)}  ${hex(register.eeprom)}  ${register.writtenByCalibration ? '*' : ' '} `,
      }))
    )
  );

  return lines.join('\n') + '\n';
}

/**
 * I²C device IDs (addresses) used to access specific memory areas and registers
 * of Circulo Integrated Encoders such as iC-MU and iC-PVL.
 *
 * These values are used as the 7-bit device address (plus R/W bit) in I²C transactions,
 * defining whether the access targets EEPROM or RAM, and whether full or limited access is allowed.
 *
 * EEPROM access allows for reading and writing of persistent configuration data,
 * while iC-PVL access allows interaction with runtime registers. Limited access
 * typically restricts operations to a subset of available registers.
 */
export enum CirculoIntegratedEncoderI2CDeviceId {
  /** I²C address for writing to EEPROM memory. */
  WRITE_EEPROM = 0xa0,

  /** I²C address for reading from EEPROM memory. */
  READ_EEPROM = 0xa1,

  /** I²C address for limited write access to iC-PVL registers. */
  WRITE_ICPVL_LIMITED = 0xc0,

  /** I²C address for limited read access to iC-PVL registers. */
  READ_ICPVL_LIMITED = 0xc1,

  /** I²C address for full write access to iC-PVL registers. */
  WRITE_ICPVL = 0xc2,

  /** I²C address for full read access to iC-PVL registers. */
  READ_ICPVL = 0xc3,
}

/**
 * A key-value object representing the `CirculoIntegratedEncoderI2CDeviceId` enum,
 * where each key is the string name of an I²C device ID, and each value is its
 * corresponding numeric address.
 *
 * This object is generated by filtering out the reverse mappings from the enum
 * (numeric keys), leaving only the name-to-value entries for use in things like
 * dropdowns or selection lists.
 *
 * @example
 * {
 *   READ_EEPROM: 161,
 *   READ_ICPVL: 195
 *   READ_ICPVL_LIMITED: 193,
 *   WRITE_EEPROM: 160,
 *   WRITE_ICPVL: 194,
 *   WRITE_ICPVL_LIMITED: 192,
 * }
 */
export const circuloIntegratedEncoderI2CDeviceIdOptions = Object.entries(CirculoIntegratedEncoderI2CDeviceId)
  .filter(([key]) => isNaN(Number(key))) // exclude numeric keys (value-to-name)
  .reduce((acc, [key, value]) => {
    acc[key] = value as number;
    return acc;
  }, {} as Record<string, number>);

export class CirculoIntegratedEncoderIcmuParameters {
  static GC_M = { address: 0x00, lsb: 6, msb: 7 };
  static GF_M = { address: 0x00, lsb: 0, msb: 5 };
  static GX_M = { address: 0x01, lsb: 0, msb: 6 };
  static VOSS_M = { address: 0x02, lsb: 0, msb: 6 };
  static VOSC_M = { address: 0x03, lsb: 0, msb: 6 };
  static PH_M = { address: 0x04, lsb: 0, msb: 6 };
  static ENAC = { address: 0x05, lsb: 7, msb: 7 };
  static CIBM = { address: 0x05, lsb: 0, msb: 3 };
  static GC_N = { address: 0x06, lsb: 6, msb: 7 };
  static GF_N = { address: 0x06, lsb: 0, msb: 5 };
  static GX_N = { address: 0x07, lsb: 0, msb: 6 };
  static VOSS_N = { address: 0x08, lsb: 0, msb: 6 };
  static VOSC_N = { address: 0x09, lsb: 0, msb: 6 };
  static PH_N = { address: 0x0a, lsb: 0, msb: 6 };
  static MODEB = { address: 0x0b, lsb: 4, msb: 6 };
  static MODEA = { address: 0x0b, lsb: 0, msb: 2 };
  static CFGEW = { address: 0x0c, lsb: 0, msb: 7 };
  static ACC_STAT = { address: 0x0d, lsb: 7, msb: 7 };
  static NCHK_CRC = { address: 0x0d, lsb: 6, msb: 6 };
  static NCHK_NON = { address: 0x0d, lsb: 5, msb: 5 };
  static ACRM_RES = { address: 0x0d, lsb: 4, msb: 4 };
  static EMTD = { address: 0x0d, lsb: 0, msb: 2 };
  static ESSI_MT = { address: 0x0e, lsb: 6, msb: 7 };
  static ROT_MT = { address: 0x0e, lsb: 5, msb: 5 };
  static LIN = { address: 0x0e, lsb: 4, msb: 4 };
  static FILT = { address: 0x0e, lsb: 0, msb: 2 };
  static SPO_MT = { address: 0x0f, lsb: 4, msb: 7 };
  static MPC = { address: 0x0f, lsb: 0, msb: 3 };
  static GET_MT = { address: 0x10, lsb: 7, msb: 7 };
  static CHK_MT = { address: 0x10, lsb: 6, msb: 6 };
  static SBL_MT = { address: 0x10, lsb: 4, msb: 5 };
  static MODE_MT = { address: 0x10, lsb: 0, msb: 3 };
  static OUT_ZERO = { address: 0x11, lsb: 5, msb: 7 };
  static OUT_MSB = { address: 0x11, lsb: 0, msb: 4 };
  static GSSI = { address: 0x12, lsb: 7, msb: 7 };
  static RSSI = { address: 0x12, lsb: 6, msb: 6 };
  static MODE_ST = { address: 0x12, lsb: 4, msb: 5 };
  static OUT_LSB = { address: 0x12, lsb: 0, msb: 3 };
  static RESABZ = { address: 0x13, lsb: 0, msb: 15 };
  static ROT = { address: 0x015, lsb: 7, msb: 7 };
  static SS_AB = { address: 0x15, lsb: 4, msb: 5 };
  static ENIF_AUTO = { address: 0x15, lsb: 3, msb: 3 };
  static FRQAB = { address: 0x15, lsb: 0, msb: 2 };
  static LENZ = { address: 0x16, lsb: 6, msb: 7 };
  static CHYS_AB = { address: 0x16, lsb: 4, msb: 5 };
  static PP60UVW = { address: 0x16, lsb: 3, msb: 3 };
  static INV_A = { address: 0x16, lsb: 2, msb: 2 };
  static INV_B = { address: 0x16, lsb: 1, msb: 1 };
  static INV_Z = { address: 0x16, lsb: 0, msb: 0 };
  static RPL = { address: 0x17, lsb: 6, msb: 7 };
  static PPUVW = { address: 0x17, lsb: 0, msb: 5 };
  static TEST = { address: 0x18, lsb: 0, msb: 7 };
  static OFF_ABZ_1 = { address: 0x1e, lsb: 4, msb: 15 };
  static OFF_POS = { address: 0x20, lsb: 0, msb: 23 };
  static OFF_COM = { address: 0x23, lsb: 4, msb: 15 };
  static PA0_CONF = { address: 0x25, lsb: 0, msb: 7 };
  static ACGAIN_M = { address: 0x2b, lsb: 3, msb: 4 };
  static AFGAIN_M = { address: 0x2b, lsb: 0, msb: 2 };
  static ACGAIN_N = { address: 0x2f, lsb: 3, msb: 4 };
  static AFGAIN_N = { address: 0x2f, lsb: 0, msb: 2 };
  static BANKSEL = { address: 0x40, lsb: 0, msb: 4 };
  static EDSBANK = { address: 0x41, lsb: 0, msb: 7 };
  static PROFILE_ID = { address: 0x42, lsb: 0, msb: 15 };
  static SERIAL = { address: 0x44, lsb: 0, msb: 31 };
  static OFF_ABZ_2 = { address: 0x48, lsb: 0, msb: 23 };
  static OFF_UVW = { address: 0x4b, lsb: 4, msb: 15 };
  static PRES_POS = { address: 0x4d, lsb: 4, msb: 39 };
  static SPO_0 = { address: 0x52, lsb: 4, msb: 7 };
  static SPO_BASE = { address: 0x52, lsb: 0, msb: 3 };
  static SPO_2 = { address: 0x53, lsb: 4, msb: 7 };
  static SPO_1 = { address: 0x53, lsb: 0, msb: 3 };
  static SPO_4 = { address: 0x54, lsb: 4, msb: 7 };
  static SPO_3 = { address: 0x54, lsb: 0, msb: 3 };
  static SPO_6 = { address: 0x55, lsb: 4, msb: 7 };
  static SPO_5 = { address: 0x55, lsb: 0, msb: 3 };
  static SPO_8 = { address: 0x56, lsb: 4, msb: 7 };
  static SPO_7 = { address: 0x56, lsb: 0, msb: 3 };
  static SPO_10 = { address: 0x57, lsb: 4, msb: 7 };
  static SPO_9 = { address: 0x57, lsb: 0, msb: 3 };
  static SPO_12 = { address: 0x58, lsb: 4, msb: 7 };
  static SPO_11 = { address: 0x58, lsb: 0, msb: 3 };
  static SPO_14 = { address: 0x59, lsb: 4, msb: 7 };
  static SPO_13 = { address: 0x59, lsb: 0, msb: 3 };
  static RPL_RESET = { address: 0x5a, lsb: 0, msb: 7 };
  static I2C_DEV_START = { address: 0x5b, lsb: 0, msb: 7 };
  static I2C_RAM_START = { address: 0x5c, lsb: 0, msb: 7 };
  static I2C_RAM_END = { address: 0x5d, lsb: 0, msb: 7 };
  static I2C_DEVID = { address: 0x5e, lsb: 0, msb: 7 };
  static I2C_RETRY = { address: 0x5f, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_1 = { address: 0x60, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_2 = { address: 0x61, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_3 = { address: 0x62, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_4 = { address: 0x63, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_5 = { address: 0x64, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_6 = { address: 0x65, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_7 = { address: 0x66, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_8 = { address: 0x67, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_9 = { address: 0x68, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_10 = { address: 0x69, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_11 = { address: 0x6a, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_12 = { address: 0x6b, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_13 = { address: 0x6c, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_14 = { address: 0x6d, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_15 = { address: 0x6e, lsb: 0, msb: 7 };
  static USER_EXCHANGE_REGISTERS_16 = { address: 0x6f, lsb: 0, msb: 7 };
  static EVENT_COUNT = { address: 0x73, lsb: 0, msb: 7 };
  static HARD_REV = { address: 0x74, lsb: 0, msb: 7 };
  static CMD_MU = { address: 0x75, lsb: 0, msb: 7 };
  static STATUS0 = { address: 0x76, lsb: 0, msb: 7 };
  static STATUS1 = { address: 0x77, lsb: 0, msb: 7 };
  static DEV_ID = { address: 0x78, lsb: 0, msb: 47 };
  static MFG_ID = { address: 0x7e, lsb: 0, msb: 15 };
}

export class CirculoIntegratedEncoderIcpvlParameters {
  static EN_PAR = { address: 0x00, lsb: 6, msb: 7 };
  static EN_ERR = { address: 0x00, lsb: 4, msb: 5 };
  static DIR = { address: 0x00, lsb: 3, msb: 3 };
  static ST_GRAY = { address: 0x00, lsb: 2, msb: 2 };
  static MT_GRAY = { address: 0x00, lsb: 1, msb: 1 };
  static INT_MODE = { address: 0x00, lsb: 0, msb: 0 };
  static OS = { address: 0x01, lsb: 5, msb: 7 };
  static MT_BW = { address: 0x01, lsb: 0, msb: 4 };
  static PCR = { address: 0x02, lsb: 0, msb: 7 };
  static EN_WRN = { address: 0x03, lsb: 7, msb: 7 };
  static BAT_MON = { address: 0x03, lsb: 6, msb: 6 };
  static A_MAX = { address: 0x03, lsb: 3, msb: 5 };
  static IBIAS = { address: 0x03, lsb: 0, msb: 2 };
  static FIXED_ZERO_1 = { address: 0x04, lsb: 7, msb: 7 };
  static NOMAG = { address: 0x04, lsb: 5, msb: 6 };
  static ATHR = { address: 0x04, lsb: 3, msb: 4 };
  static ONAX = { address: 0x04, lsb: 2, msb: 2 };
  static POLEWID = { address: 0x04, lsb: 0, msb: 1 };
  static I2C_POS = { address: 0x05, lsb: 7, msb: 7 };
  static PCR_OUT = { address: 0x05, lsb: 6, msb: 6 };
  static SYNC_BW = { address: 0x05, lsb: 4, msb: 5 };
  static BAT_THR = { address: 0x05, lsb: 2, msb: 3 };
  static HYS = { address: 0x05, lsb: 1, msb: 1 };
  static ABQUAD = { address: 0x05, lsb: 0, msb: 0 };
  static CRC_CFG = { address: 0x06, lsb: 0, msb: 7 };
  static MT_COUNT = { address: 0x07, lsb: 0, msb: 39 };
  static CRC_CTR = { address: 0x0c, lsb: 0, msb: 7 };
  static FIXED_ZERO_2 = { address: 0x0d, lsb: 3, msb: 7 };
  static SYNC = { address: 0x0d, lsb: 0, msb: 2 };
  static CHIP_REL = { address: 0x0f, lsb: 0, msb: 7 };
  static PRESET = { address: 0x10, lsb: 7, msb: 7 };
  static PDR = { address: 0x10, lsb: 6, msb: 6 };
  static BAT_WRN = { address: 0x10, lsb: 5, msb: 5 };
  static BAT_ERR = { address: 0x10, lsb: 4, msb: 4 };
  static POS_ERR = { address: 0x10, lsb: 3, msb: 3 };
  static CTR_ERR = { address: 0x10, lsb: 2, msb: 2 };
  static CFG_ERR = { address: 0x10, lsb: 1, msb: 1 };
  static STUP_ERR = { address: 0x10, lsb: 0, msb: 0 };
  static CMD = { address: 0x11, lsb: 0, msb: 7 };
  static SLEEP_ST = { address: 0x12, lsb: 7, msb: 7 };
  static NOMAG_ST = { address: 0x12, lsb: 6, msb: 6 };
  static ACTIVE_ST = { address: 0x12, lsb: 5, msb: 5 };
  static POWON_ST = { address: 0x12, lsb: 4, msb: 4 };
  static NOMAG_L = { address: 0x12, lsb: 2, msb: 2 };
  static MAG_ERR = { address: 0x12, lsb: 1, msb: 1 };
  static AMPL_ERR = { address: 0x12, lsb: 0, msb: 0 };
}

export class CirculoIntegratedEncoderEepromParameters {
  // iC-MU registers
  static GC_M = { address: 0x00, lsb: 6, msb: 7 };
  static GF_M = { address: 0x00, lsb: 0, msb: 5 };
  static GX_M = { address: 0x01, lsb: 0, msb: 6 };
  static VOSS_M = { address: 0x02, lsb: 0, msb: 6 };
  static VOSC_M = { address: 0x03, lsb: 0, msb: 6 };
  static PH_M = { address: 0x04, lsb: 0, msb: 6 };
  static ENAC = { address: 0x05, lsb: 7, msb: 7 };
  static CIBM = { address: 0x05, lsb: 0, msb: 3 };
  static GC_N = { address: 0x06, lsb: 6, msb: 7 };
  static GF_N = { address: 0x06, lsb: 0, msb: 5 };
  static GX_N = { address: 0x07, lsb: 0, msb: 6 };
  static VOSS_N = { address: 0x08, lsb: 0, msb: 6 };
  static VOSC_N = { address: 0x09, lsb: 0, msb: 6 };
  static PH_N = { address: 0x0a, lsb: 0, msb: 6 };
  static MODEB = { address: 0x0b, lsb: 4, msb: 6 };
  static MODEA = { address: 0x0b, lsb: 0, msb: 2 };
  static CFGEW = { address: 0x0c, lsb: 0, msb: 7 };
  static ACC_STAT = { address: 0x0d, lsb: 7, msb: 7 };
  static NCHK_CRC = { address: 0x0d, lsb: 6, msb: 6 };
  static NCHK_NON = { address: 0x0d, lsb: 5, msb: 5 };
  static ACRM_RES = { address: 0x0d, lsb: 4, msb: 4 };
  static EMTD = { address: 0x0d, lsb: 0, msb: 2 };
  static ESSI_MT = { address: 0x0e, lsb: 6, msb: 7 };
  static ROT_MT = { address: 0x0e, lsb: 5, msb: 5 };
  static LIN = { address: 0x0e, lsb: 4, msb: 4 };
  static FILT = { address: 0x0e, lsb: 0, msb: 2 };
  static SPO_MT = { address: 0x0f, lsb: 4, msb: 7 };
  static MPC = { address: 0x0f, lsb: 0, msb: 3 };
  static GET_MT = { address: 0x10, lsb: 7, msb: 7 };
  static CHK_MT = { address: 0x10, lsb: 6, msb: 6 };
  static SBL_MT = { address: 0x10, lsb: 4, msb: 5 };
  static MODE_MT = { address: 0x10, lsb: 0, msb: 3 };
  static OUT_ZERO = { address: 0x11, lsb: 5, msb: 7 };
  static OUT_MSB = { address: 0x11, lsb: 0, msb: 4 };
  static GSSI = { address: 0x12, lsb: 7, msb: 7 };
  static RSSI = { address: 0x12, lsb: 6, msb: 6 };
  static MODE_ST = { address: 0x12, lsb: 4, msb: 5 };
  static OUT_LSB = { address: 0x12, lsb: 0, msb: 3 };
  static RESABZ = { address: 0x13, lsb: 0, msb: 15 };
  static ROT = { address: 0x015, lsb: 7, msb: 7 };
  static SS_AB = { address: 0x15, lsb: 4, msb: 5 };
  static ENIF_AUTO = { address: 0x15, lsb: 3, msb: 3 };
  static FRQAB = { address: 0x15, lsb: 0, msb: 2 };
  static LENZ = { address: 0x16, lsb: 6, msb: 7 };
  static CHYS_AB = { address: 0x16, lsb: 4, msb: 5 };
  static PP60UVW = { address: 0x16, lsb: 3, msb: 3 };
  static INV_A = { address: 0x16, lsb: 2, msb: 2 };
  static INV_B = { address: 0x16, lsb: 1, msb: 1 };
  static INV_Z = { address: 0x16, lsb: 0, msb: 0 };
  static RPL = { address: 0x17, lsb: 6, msb: 7 };
  static PPUVW = { address: 0x17, lsb: 0, msb: 5 };
  static TEST = { address: 0x18, lsb: 0, msb: 7 };
  static SPO_0 = { address: 0x19, lsb: 4, msb: 7 };
  static SPO_BASE = { address: 0x19, lsb: 0, msb: 3 };
  static SPO_2 = { address: 0x1a, lsb: 4, msb: 7 };
  static SPO_1 = { address: 0x1a, lsb: 0, msb: 3 };
  static SPO_4 = { address: 0x1b, lsb: 4, msb: 7 };
  static SPO_3 = { address: 0x1b, lsb: 0, msb: 3 };
  static SPO_6 = { address: 0x1c, lsb: 4, msb: 7 };
  static SPO_5 = { address: 0x1c, lsb: 0, msb: 3 };
  static SPO_8 = { address: 0x1d, lsb: 4, msb: 7 };
  static SPO_7 = { address: 0x1d, lsb: 0, msb: 3 };
  static SPO_10 = { address: 0x1e, lsb: 4, msb: 7 };
  static SPO_9 = { address: 0x1e, lsb: 0, msb: 3 };
  static SPO_12 = { address: 0x1f, lsb: 4, msb: 7 };
  static SPO_11 = { address: 0x1f, lsb: 0, msb: 3 };
  static SPO_14 = { address: 0x20, lsb: 4, msb: 7 };
  static SPO_13 = { address: 0x20, lsb: 0, msb: 3 };
  static CRC16 = { address: 0x21, lsb: 0, msb: 15 };
  static OFF_ABZ = { address: 0x23, lsb: 4, msb: 39 };
  static OFF_UVW = { address: 0x28, lsb: 4, msb: 15 };
  static PRES_POS = { address: 0x2a, lsb: 4, msb: 39 };
  static CRC8 = { address: 0x2f, lsb: 0, msb: 7 };
  static PA0_CONF = { address: 0x30, lsb: 0, msb: 7 };
  static EDSBANK = { address: 0x31, lsb: 0, msb: 7 };
  static PROFILE_ID = { address: 0x32, lsb: 0, msb: 15 };
  static SERIAL = { address: 0x34, lsb: 0, msb: 31 };
  static DEV_ID = { address: 0x38, lsb: 0, msb: 47 };
  static MFG_ID = { address: 0x3e, lsb: 0, msb: 15 };
  // iC-PVL registers
  static EN_PAR = { address: 0x40, lsb: 6, msb: 7 };
  static EN_ERR = { address: 0x40, lsb: 4, msb: 5 };
  static DIR = { address: 0x40, lsb: 3, msb: 3 };
  static ST_GRAY = { address: 0x40, lsb: 2, msb: 2 };
  static MT_GRAY = { address: 0x40, lsb: 1, msb: 1 };
  static INT_MODE = { address: 0x40, lsb: 0, msb: 0 };
  static OS = { address: 0x41, lsb: 5, msb: 7 };
  static MT_BW = { address: 0x41, lsb: 0, msb: 4 };
  static PCR = { address: 0x42, lsb: 0, msb: 7 };
  static EN_WRN = { address: 0x43, lsb: 7, msb: 7 };
  static BAT_MON = { address: 0x43, lsb: 6, msb: 6 };
  static A_MAX = { address: 0x43, lsb: 3, msb: 5 };
  static IBIAS = { address: 0x43, lsb: 0, msb: 2 };
  static FIXED_ZERO_1 = { address: 0x44, lsb: 7, msb: 7 };
  static NOMAG = { address: 0x44, lsb: 5, msb: 6 };
  static ATHR = { address: 0x44, lsb: 3, msb: 4 };
  static ONAX = { address: 0x44, lsb: 2, msb: 2 };
  static POLEWID = { address: 0x44, lsb: 0, msb: 1 };
  static I2C_POS = { address: 0x45, lsb: 7, msb: 7 };
  static PCR_OUT = { address: 0x45, lsb: 6, msb: 6 };
  static SYNC_BW = { address: 0x45, lsb: 4, msb: 5 };
  static BAT_THR = { address: 0x45, lsb: 2, msb: 3 };
  static HYS = { address: 0x45, lsb: 1, msb: 1 };
  static ABQUAD = { address: 0x45, lsb: 0, msb: 0 };
  static CRC_CFG = { address: 0x46, lsb: 0, msb: 7 };
  static MT_PREL = { address: 0x47, lsb: 0, msb: 39 };
  static CRC_CTR = { address: 0x4c, lsb: 0, msb: 7 };
}

/**
 * Commands for the iC-MU encoder's `CMD_MU` register (address 0x75).
 *
 * These command codes control various internal operations of the iC-MU encoder,
 * such as memory writes, resets, communication settings, and maintenance counters.
 */
export class CirculoIntegratedEncoderIcmuCommands {
  /** Write all configuration settings to EEPROM. */
  static WRITE_ALL = 0x01;

  /** Disable EEPROM write access. */
  static WRITE_OFF = 0x02;

  /** Reset absolute position data. */
  static ABS_RESET = 0x03;

  /** Start non-volatile memory verification. */
  static NON_VER = 0x04;

  /** Reset multiturn counter. */
  static MT_RESET = 0x05;

  /** Verify multiturn counter value. */
  static MT_VER = 0x06;

  /** Perform a software reset of the device. */
  static SOFT_RESET = 0x07;

  /** Perform a software presence check. */
  static SOFT_PRES = 0x08;

  /** Perform a software + EEPROM presence check. */
  static SOFT_E2P_PRES = 0x09;

  /** Set or test I²C communication path. */
  static I2C_COM = 0x0a;

  /** Trigger an event counter update. */
  static EVENT_COUNT = 0x0b;

  /** Toggle or test switch functionality. */
  static SWITCH = 0x0c;

  /** Verify stored CRC against calculated CRC. */
  static CRC_VER = 0x0d;

  /** Trigger CRC calculation. */
  static CRC_CALC = 0x0e;

  /** Set multiturn counter (manual value assignment). */
  static SET_MTC = 0x0f;

  /** Reset multiturn counter to zero. */
  static RES_MTC = 0x10;
}

/**
 * Commands for the iC-PVL encoder's `CMD` register (address 0x11).
 *
 * These command values are written to the `CMD` register to control
 * the behavior of the encoder, including reset, sleep mode, and clearing status.
 */
export class CirculoIntegratedEncoderIcpvlCommands {
  /** Perform a software reset of the device. */
  static RESET = 0x02;

  /** Reboot the device, similar to power cycling. */
  static REBOOT = 0x03;

  /** Enable sleep mode to reduce power consumption. */
  static SLEEP_ENABLE = 0x04;

  /** Clear internal status flags or errors (software clear). */
  static SCLR = 0x05;

  /** Disable sleep mode and resume normal operation. */
  static SLEEP_DISABLE = 0x07;
}

/**
 * Returns a color representing the encoder calibration quality for Circulo devices.
 *
 * The color is determined by the maximum-value percentage:
 *
 * - `50–100%`: **Green** (`#6ba853`) — robust calibration
 * - `20–50%`: **Yellow** (`#aeb303`) — acceptable, small displacements tolerated
 * - `0–20%`: **Orange** (`#e69138`) — weak, avoid mechanical displacement
 * - `≤ 0%`: **Red** (`#fb262e`) — invalid calibration, phase error too high
 *
 * @param percentage - The maximum-value percentage obtained from encoder calibration.
 * @returns A hex color string representing the calibration quality, or an empty string if out of range.
 */
export function getCirculoEncoderCalibrationMaxValuePercentageColor(percentage: number) {
  if (percentage >= 50 && percentage <= 100) {
    return '#6ba853';
  } else if (percentage > 20 && percentage < 50) {
    return '#aeb303';
  } else if (percentage > 0 && percentage <= 20) {
    return '#e69138';
  } else if (percentage <= 0) {
    return '#fb262e';
  }
  return '';
}

/**
 * Returns a descriptive label explaining the quality of the encoder calibration
 * based on the maximum-value percentage for Circulo devices.
 *
 * The label communicates how robust the calibration is with respect to mechanical
 * displacement and thermal expansion:
 *
 * - `50–100%`: Robust against displacement and temperature changes
 * - `20–50%`: Robust to small mechanical or thermal variations
 * - `0–20%`: Calibration succeeded but sensitive; avoid displacement
 * - `≤ 0%`: Calibration failed; phase error too high
 *
 * @param percentage - The maximum-value percentage obtained from encoder calibration.
 * @returns A human-readable label describing calibration quality, or an empty string if out of range.
 */
export function getCirculoEncoderCalibrationMaxValuePercentageLabel(percentage: number) {
  if (percentage >= 50 && percentage <= 100) {
    return 'Calibration was successful. Encoder system is robust to mechanical displacements and temperature expansions.';
  } else if (percentage > 20 && percentage < 50) {
    return 'Calibration was successful. Encoder system is robust to small mechanical displacements and temperature expansions.';
  } else if (percentage > 0 && percentage <= 20) {
    return 'Calibration was successful. Mechanical displacement and temperature expansions should be avoided.';
  } else if (percentage <= 0) {
    return "Phase error is too high. The absolute position can't be computed reliably. Adjust mechanical position and repeat calibration.";
  }
  return '';
}

/**
 * External Circulo encoder types accepted by the Circulo encoder narrow angle calibration procedure.
 *
 * The values mirror the enum the Motion Master proto file keeps commented out. They name the
 * magnetic ring of a Circulo mounted on another drive; an integrated ring is left at
 * {@link CirculoExternalEncoderType.NONE}.
 */
export enum CirculoExternalEncoderType {
  NONE = 0,
  C7_INNER = 1,
  C7_OUTER = 2,
  C9_INNER = 3,
  C9_OUTER = 4,
}

/** Max permissible phase error of a Circulo 7 magnetic ring, in encoder increments. */
export const circulo7MaxPermissiblePhaseError = 256;

/** Max permissible phase error of a Circulo 9 magnetic ring, in encoder increments. */
export const circulo9MaxPermissiblePhaseError = 128;

/**
 * Returns the max permissible phase error of the ring a calibration measures, derived from the product, in
 * encoder increments.
 *
 * Motion Master 5.8.3 and later report the limit of the measured ring in every calibration progress status.
 * This function is the fallback for an older Motion Master, which reports none. It only knows two rings: a
 * Circulo 7 ring tolerates 256 increments and a Circulo 9 ring 128. Any other ring gets 128, which may not
 * be its limit.
 *
 * An external encoder type names the ring outright, so it wins over the product: that ring sits on a
 * drive of an unrelated size. Everything else is answered by the product group of the device itself,
 * which leaves a Circulo 9, a Circulo NG and an unrecognised product on 128.
 *
 * @param productId - The device carrying the encoder: a product id, a serial number or a `HardwareDescription`
 * @param externalEncoderType - The external ring the procedure was asked to calibrate, if any
 * @returns The max permissible phase error in encoder increments
 */
export function getCirculoEncoderMaxPermissiblePhaseError(
  productId?: number | string | HardwareDescription | null,
  externalEncoderType: CirculoExternalEncoderType | number = CirculoExternalEncoderType.NONE
): number {
  if (
    externalEncoderType === CirculoExternalEncoderType.C7_INNER ||
    externalEncoderType === CirculoExternalEncoderType.C7_OUTER
  ) {
    return circulo7MaxPermissiblePhaseError;
  }

  if (
    externalEncoderType === CirculoExternalEncoderType.C9_INNER ||
    externalEncoderType === CirculoExternalEncoderType.C9_OUTER
  ) {
    return circulo9MaxPermissiblePhaseError;
  }

  return findSomanetProductById(resolveEncoderProductId(productId))?.group === 'Circulo 7'
    ? circulo7MaxPermissiblePhaseError
    : circulo9MaxPermissiblePhaseError;
}

/**
 * Result of Circulo encoder calibration computation.
 */
export interface CirculoEncoderCalibrationResult {
  /** Paired data points: [position, phaseError] */
  data: number[][];
  /** Maximum-value percentage indicating calibration quality (0–100) */
  value: number;
  /** Human-readable meaning of the calibration percentage */
  meaning: string;
  /** Max permissible phase error the percentage was computed against, in encoder increments */
  maxPermissiblePhaseError: number;
}

/**
 * Computes the Circulo encoder calibration result from position and phaseError arrays.
 *
 * Handles empty or mismatched inputs gracefully by returning null.
 *
 * @param position - Array of encoder positions
 * @param phaseError - Array of corresponding phase errors
 * @param maxPermissiblePhaseError - Max permissible phase error of the measured ring, in encoder increments.
 *   The percentage is the measured phase error expressed against this limit, so the limit of another ring
 *   misreports the quality: a Circulo 9 ring scored against the Circulo 7 limit reads roughly twice the
 *   headroom it has. The limit comes back in the result.
 * @returns Calibration result or null if inputs are invalid or empty
 */
export function computeCirculoEncoderCalibrationResult(
  position: number[] | undefined,
  phaseError: number[] | undefined,
  maxPermissiblePhaseError: number
): CirculoEncoderCalibrationResult | null {
  // Return null if inputs are missing or lengths don't match
  if (!position || !phaseError || position.length !== phaseError.length || position.length === 0) {
    return null;
  }

  // Pair valid [position, phaseError] points
  const data: number[][] = position
    .map((p, i) => {
      const err = phaseError[i];
      return Number.isFinite(p) && Number.isFinite(err) ? [p, err] : undefined;
    })
    .filter((pt): pt is number[] => !!pt);

  if (data.length === 0) {
    return null;
  }

  // Find the data point with the largest absolute phase error
  const maxPoint = data.reduce((max, point) => (Math.abs(point[1]) > Math.abs(max[1]) ? point : max), data[0]);

  const maxValuePercentage = Math.max(100 - Math.floor((Math.abs(maxPoint[1]) / maxPermissiblePhaseError) * 100), 0);

  return {
    data,
    value: maxValuePercentage,
    meaning: getCirculoEncoderCalibrationMaxValuePercentageLabel(maxValuePercentage),
    maxPermissiblePhaseError,
  };
}

/**
 * Computes the Circulo encoder calibration result of one calibration iteration.
 *
 * The result is scored against the limit the progress status carries. Motion Master 5.8.3 and later report
 * it, and `startCirculoEncoderNarrowAngleCalibrationProcedure` fills it in for an older Motion Master. This
 * is the limit OBLAC Drives scores against, so both show the same percentage.
 *
 * @param progress - Progress of a Circulo encoder narrow angle calibration status
 * @returns Calibration result, or null if the progress carries no data or no limit
 */
export function computeCirculoEncoderCalibrationProgressResult(
  progress?: MotionMasterMessage.Status.CirculoEncoderNarrowAngleCalibrationProcedure.IProgress | null
): CirculoEncoderCalibrationResult | null {
  const maxPermissiblePhaseError = progress?.maxPermissiblePhaseError ?? 0;

  if (!progress || maxPermissiblePhaseError <= 0) {
    return null;
  }

  return computeCirculoEncoderCalibrationResult(
    progress.position ?? undefined,
    progress.phaseError ?? undefined,
    maxPermissiblePhaseError
  );
}
